import { Directive, ElementRef, HostListener, Renderer2 } from '@angular/core';

const TILE_CLASS = 'logo-tile';

/**
 * Adds the `logo-tile` class to a logo <img> whose corners are opaque and near-white - a logo
 * exported with its own white background (e.g. a JPEG) - so it can be shown on a rounded white
 * tile instead of as a hard-edged rectangle. Transparent logos are left untouched, since a tile
 * would hide a white logo. If the image host blocks pixel reads (no CORS), nothing is added.
 */
@Directive({
  selector: 'img[appLogoTile]',
  standalone: true,
})
export class LogoTileDirective {
  constructor(private el: ElementRef<HTMLImageElement>, private renderer: Renderer2) {}

  @HostListener('load')
  onLoad() {
    const src = this.el.nativeElement.currentSrc || this.el.nativeElement.src;
    this.renderer.removeClass(this.el.nativeElement, TILE_CLASS);
    hasOpaqueLightCorners(src).then(tile => {
      if (tile && src === (this.el.nativeElement.currentSrc || this.el.nativeElement.src)) {
        this.renderer.addClass(this.el.nativeElement, TILE_CLASS);
      }
    });
  }
}

function hasOpaqueLightCorners(url: string): Promise<boolean> {
  return new Promise(resolve => {
    if (!url) {
      resolve(false);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(false);
    img.onload = () => {
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const corners = [[0, 0], [size - 1, 0], [0, size - 1], [size - 1, size - 1]];
        const light = corners.filter(([x, y]) => {
          const [r, g, b, a] = ctx.getImageData(x, y, 1, 1).data;
          return a > 200 && r > 225 && g > 225 && b > 225;
        });
        resolve(light.length >= 3);
      } catch (e) {
        resolve(false);
      }
    };
    img.src = url;
  });
}
